import { useCallback, useEffect, useRef, useState } from 'react';

import MessageBubble from 'components/_shared/MessageBubble';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
    questionBody: {
        marginBottom: '100px'
    },
    suggestionContainer: {
        display: 'flex',
        flexDirection: 'column',
        background: '#FFFFFF',
        borderRadius: '20px',
        padding: '20px 30px',
        width: '75%',
        margin: 'auto',
        boxShadow: '0px 10px 80px rgba(0, 0, 0, 0.1)',
        position: 'relative'
    },
    suggestionTitle: {
        fontSize: '14px',
        fontWeight: '800',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        background: '#8882D8',
        borderRadius: '32px',
        padding: '5px 20px',
        color: '#FFFFFF',
        position: 'relative',
        top: '0.5em',
        left: '11%',
        zIndex: '2'
    },
    answerDiv: {
        width: "100%",
        padding: "1rem 0",
        boxSizing: "border-box",
    },
    textarea: {
        fontFamily: 'Manrope',
        fontSize: '14px',
        color: '#1F2152',
        backgroundColor: "#FFFFFF",
        border: "1px solid #E3E3FA",
        borderRadius: "15px",
        width: "100%",
        height: "200px",
        padding: "1rem",
        resize: "none",
        outline: "none",
        overflow: "auto",
        boxSizing: "border-box",
        "&:focus": {
            borderColor: "#7378e8",
        },
    },
    disclaimer: {
        width: '100%',
        border: '0',
        padding: '0px 0px 0px 0px',
        fontFamily: 'Manrope',
        fontSize: '.75rem',
        fontWeight: '200',
        color: '#000000',
        resize: 'none',
        fontStyle: 'italic'
    }
}));

const useDebounce = (callback, delay) => {
    const debounceRef = useRef(null);

    const debouncedCallback = useCallback(
        (...args) => {
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                callback(...args);
            }, delay);
        },
        [callback, delay]
    );

    return debouncedCallback;
};

const FreeResponseQuestion = ({
    setAnswers,
    name,
    questions,
    questionLength,
    questionNum,
    answers,
    showPublicFacingMessage
}) => {
    const classes = useStyles();
    const questionRef = useRef(null);

    const [isActive, setIsActive] = useState(false);
    const { question, id, quality_of_life_measure_id } = questions;

    const handleIntersectionDebounced = useDebounce(
        ([entry]) => {
            setIsActive(entry.isIntersecting);
        },
        200 // Adjust the debounce delay as needed
    );

    const currentAnswer =
        answers.find(a => a.touchpoint_question_id === id)?.answer || "";

    const handleChange = (value) => {
        setAnswers(prev => {
            const existingIndex = prev.findIndex(
                a => a.touchpoint_question_id === id
            );
    
            if (existingIndex !== -1) {
                const updated = [...prev];
                updated[existingIndex] = {
                    ...updated[existingIndex],
                    answer: value
                };
                return updated;
            }
    
            return [...prev, { touchpoint_question_id: id, answer: value, quality_of_life_measure_id }];
        });
    };

    useEffect(() => {
        const observer = new IntersectionObserver(handleIntersectionDebounced, {
            threshold: 0.7
        });
        const questionActive = questionRef.current;

        if (questionActive) {
            observer.observe(questionActive);
        }

        return () => {
            if (questionActive) {
                observer.unobserve(questionActive);
            }
        };
    }, [handleIntersectionDebounced]);

    return (
        <div
            className={classes.questionBody}
            ref={questionRef}
            id={id}
            style={{ opacity: isActive ? 1 : 0.4, transition: 'opacity 0.4s' }}
        >
            <MessageBubble
                name={name}
                question={question}
                questionLength={questionLength}
                questionNum={questionNum}
            />
            <span className={classes.suggestionTitle}>
                Write Response
            </span>
            <div className={classes.suggestionContainer}>
                <div className={classes.answerDiv}>
                    <textarea
                        id={`answer-${id}`}
                        className={classes.textarea}
                        value={currentAnswer}
                        onChange={(e) => handleChange(e.target.value)}
                        placeholder="Write your answer here..."
                        rows={5}
                    />
                </div>
                { showPublicFacingMessage &&
                    <span className={classes.disclaimer}>* This answer will publicly visible on your Samaritan profile</span>
                }
            </div>
        </div>
    );
};

export default FreeResponseQuestion;
